/**
 * Indice de la red familiar.
 *
 * El modelo guarda las relaciones como las guarda Gramps: a traves de
 * familias, y de forma redundante (childof / parentin / childref). Trabajar
 * con eso directamente es incomodo y lento. Este indice lo recorre una vez y
 * deja a mano lo que la aplicacion pregunta todo el rato: padres, hijos,
 * hermanos, parejas, ascendientes.
 *
 * Se reconstruye entero cuando el arbol cambia. Con 80 personas eso cuesta
 * microsegundos; con varios miles sigue siendo despreciable al lado de un
 * cuadro de dibujo.
 */
import type { Arbol, Familia, Persona } from "./model.js";

/** Una pareja de alguien, con la familia que las une. */
export interface Vinculo {
  /** La otra persona. */
  otra: string;
  familia: string;
  /** "Married" | "Unmarried" | "Unknown" | ... */
  rel: string;
}

/** De quien es hijo alguien. Cualquiera de los dos puede faltar. */
export interface Progenitores {
  padre?: string;
  madre?: string;
  /** La familia por la que es hijo. */
  familia: string;
}

/** Como se comparte la hermandad. */
export type ClaseHermandad =
  /** Comparten padre y madre. */
  | "completos"
  /** Solo el padre. */
  | "medio-paterno"
  /** Solo la madre. */
  | "medio-materno"
  /**
   * Comparten familia pero la familia no tiene progenitores registrados.
   * Es el patron estandar de Gramps para declarar hermandad sin conocer a los
   * padres, y el arbol real lo usa.
   */
  | "declarados";

export interface Hermano {
  otra: string;
  clase: ClaseHermandad;
}

/** Un ascendiente alcanzado, con a que distancia y por que rama. */
export interface Ascendiente {
  /** Generaciones hacia arriba. 1 = padre o madre. */
  dist: number;
  /** Si el camino pasa por el padre de la persona de partida. */
  viaPadre: boolean;
  /** Si pasa por la madre. */
  viaMadre: boolean;
}

export class Indice {
  readonly arbol: Arbol;

  /** Familias donde la persona es hija. Normalmente una, puede haber mas. */
  private readonly comoHijo = new Map<string, string[]>();
  /** Familias donde la persona es progenitora. Varias = medios hermanos. */
  private readonly comoProgenitor = new Map<string, string[]>();
  /** Hijos directos, sin repetir. */
  private readonly hijos = new Map<string, string[]>();
  private readonly vinculos = new Map<string, Vinculo[]>();

  constructor(arbol: Arbol) {
    this.arbol = arbol;

    // Se parte de las familias, no de los campos redundantes de la persona:
    // <family> es la fuente de verdad y asi un fichero con las tres
    // relaciones descuadradas no produce un indice incoherente. De los
    // descuadres avisa validar.ts, que es su trabajo.
    for (const f of arbol.familias.values()) {
      const progenitores = [f.padre, f.madre].filter(
        (x): x is string => !!x && arbol.personas.has(x),
      );

      for (const p of progenitores) empujar(this.comoProgenitor, p, f.handle);

      if (progenitores.length === 2) {
        const [a, b] = progenitores as [string, string];
        empujar(this.vinculos, a, { otra: b, familia: f.handle, rel: f.rel ?? "Unknown" });
        empujar(this.vinculos, b, { otra: a, familia: f.handle, rel: f.rel ?? "Unknown" });
      }

      for (const ref of f.hijos) {
        const h = ref.hlink;
        if (!arbol.personas.has(h)) continue;
        empujar(this.comoHijo, h, f.handle);
        for (const p of progenitores) {
          const ls = this.hijos.get(p);
          if (ls) {
            if (!ls.includes(h)) ls.push(h);
          } else {
            this.hijos.set(p, [h]);
          }
        }
      }
    }
  }

  persona(h: string): Persona | undefined {
    return this.arbol.personas.get(h);
  }

  familia(h: string): Familia | undefined {
    return this.arbol.familias.get(h);
  }

  /** Todas las personas, en el orden del fichero. */
  todas(): string[] {
    return [...this.arbol.personas.keys()];
  }

  familiasComoHijo(h: string): string[] {
    return this.comoHijo.get(h) ?? [];
  }

  familiasComoProgenitor(h: string): string[] {
    return this.comoProgenitor.get(h) ?? [];
  }

  /**
   * De quien es hijo. Devuelve una entrada por familia: si alguien figura
   * como hijo en dos familias hay dos, y de eso avisa validar.ts.
   */
  progenitores(h: string): Progenitores[] {
    return this.familiasComoHijo(h).map((fh) => {
      const f = this.arbol.familias.get(fh);
      return { padre: f?.padre, madre: f?.madre, familia: fh };
    });
  }

  /** El padre y la madre "principales": los de la primera familia. */
  padreYmadre(h: string): { padre?: string; madre?: string } {
    const primera = this.progenitores(h)[0];
    return { padre: primera?.padre, madre: primera?.madre };
  }

  /** Los progenitores de alguien, sin repetir, como lista de handles. */
  progenitoresPlanos(h: string): string[] {
    const out: string[] = [];
    for (const g of this.progenitores(h)) {
      for (const x of [g.padre, g.madre]) {
        if (x && !out.includes(x)) out.push(x);
      }
    }
    return out;
  }

  hijosDe(h: string): string[] {
    return this.hijos.get(h) ?? [];
  }

  /** Las parejas de alguien. Varias entradas = varias uniones. */
  parejas(h: string): Vinculo[] {
    return this.vinculos.get(h) ?? [];
  }

  /**
   * Los hermanos, clasificados. Incluye medios hermanos y los declarados por
   * una familia sin progenitores.
   */
  hermanos(h: string): Hermano[] {
    const out = new Map<string, Hermano>();

    for (const fh of this.familiasComoHijo(h)) {
      const f = this.arbol.familias.get(fh);
      if (!f) continue;
      for (const ref of f.hijos) {
        const o = ref.hlink;
        if (o === h || !this.arbol.personas.has(o)) continue;
        const clase = this.claseHermandad(h, o);
        if (!clase) continue;
        const previo = out.get(o);
        // Si aparecen por varias vias, gana la mas cercana.
        if (!previo || rango(clase) < rango(previo.clase)) out.set(o, { otra: o, clase });
      }
    }

    // Medios hermanos que NO comparten familia: cuando un progenitor tiene
    // hijos en dos familias distintas, los hijos de una no figuran en la
    // otra. Hay que ir por los progenitores.
    for (const g of this.progenitoresPlanos(h)) {
      for (const o of this.hijosDe(g)) {
        if (o === h) continue;
        const clase = this.claseHermandad(h, o);
        if (!clase) continue;
        const previo = out.get(o);
        if (!previo || rango(clase) < rango(previo.clase)) out.set(o, { otra: o, clase });
      }
    }

    return [...out.values()];
  }

  /**
   * Como son hermanos dos personas, o undefined si no lo son.
   *
   * Aqui esta la parte delicada: se compara por progenitor concreto, no por
   * familia, porque con uniones multiples dos personas pueden compartir madre
   * estando en familias distintas.
   */
  claseHermandad(a: string, b: string): ClaseHermandad | undefined {
    if (a === b) return undefined;

    // Primero: si figuran como hijos de la MISMA familia, son hermanos
    // completos, tenga esa familia los dos progenitores o solo uno.
    //
    // Esto hay que mirarlo antes que nada. Comparando progenitor a progenitor,
    // dos hijos de una familia con madre registrada y padre desconocido salen
    // como "medios hermanos por parte de madre", y es falso: el padre no es
    // distinto, es que no se sabe quien es. En el arbol real hay siete
    // familias con solo madre y dos con solo padre, asi que el error afectaba
    // a bastante gente.
    const compartidas = this.familiasComoHijo(a).filter((f) =>
      this.familiasComoHijo(b).includes(f),
    );
    for (const fh of compartidas) {
      const f = this.arbol.familias.get(fh);
      if (f && (f.padre || f.madre)) return "completos";
    }

    const padres = (x: string): { padres: Set<string>; madres: Set<string> } => {
      const ps = new Set<string>();
      const ms = new Set<string>();
      for (const g of this.progenitores(x)) {
        if (g.padre) ps.add(g.padre);
        if (g.madre) ms.add(g.madre);
      }
      return { padres: ps, madres: ms };
    };

    const ga = padres(a);
    const gb = padres(b);
    const mismoPadre = [...ga.padres].some((x) => gb.padres.has(x));
    const mismaMadre = [...ga.madres].some((x) => gb.madres.has(x));

    if (mismoPadre && mismaMadre) return "completos";
    if (mismoPadre) return "medio-paterno";
    if (mismaMadre) return "medio-materno";

    // Sin progenitores en comun: solo son hermanos si una familia sin
    // progenitores los declara como tales.
    for (const fh of this.familiasComoHijo(a)) {
      if (!this.familiasComoHijo(b).includes(fh)) continue;
      const f = this.arbol.familias.get(fh);
      if (f && !f.padre && !f.madre) return "declarados";
    }
    return undefined;
  }

  /**
   * Todos los ascendientes de alguien, con la distancia minima y por que
   * rama se llega.
   *
   * Tolera ciclos (alguien antepasado de si mismo), que el fichero no deberia
   * tener pero podria: se recorre con distancias minimas y sin repetir.
   */
  ascendientes(h: string): Map<string, Ascendiente> {
    const out = new Map<string, Ascendiente>();

    // Se arranca por separado desde el padre y desde la madre para poder
    // marcar la rama. Los progenitores de familias adicionales se tratan como
    // rama del sexo que corresponda.
    const semillas: Array<[string, boolean, boolean]> = [];
    for (const g of this.progenitores(h)) {
      if (g.padre) semillas.push([g.padre, true, false]);
      if (g.madre) semillas.push([g.madre, false, true]);
    }

    let frente: Array<[string, boolean, boolean]> = semillas;
    let dist = 1;
    const vistos = new Set<string>([h]);

    while (frente.length && dist < 200) {
      const siguiente: Array<[string, boolean, boolean]> = [];
      for (const [x, vp, vm] of frente) {
        const ya = out.get(x);
        if (ya) {
          // Ya alcanzado por un camino mas corto: solo se acumula la rama.
          ya.viaPadre ||= vp;
          ya.viaMadre ||= vm;
          continue;
        }
        out.set(x, { dist, viaPadre: vp, viaMadre: vm });
        if (vistos.has(x)) continue;
        vistos.add(x);
        for (const g of this.progenitoresPlanos(x)) siguiente.push([g, vp, vm]);
      }
      frente = siguiente;
      dist++;
    }
    return out;
  }

  /** Todos los descendientes, con la distancia minima. */
  descendientes(h: string): Map<string, number> {
    const out = new Map<string, number>();
    let frente = [h];
    let dist = 0;
    const vistos = new Set<string>();
    while (frente.length && dist < 200) {
      const siguiente: string[] = [];
      for (const x of frente) {
        if (vistos.has(x)) continue;
        vistos.add(x);
        if (dist > 0 && !out.has(x)) out.set(x, dist);
        for (const c of this.hijosDe(x)) siguiente.push(c);
      }
      frente = siguiente;
      dist++;
    }
    return out;
  }

  /**
   * Vecinos para resaltar al pasar el cursor: progenitores, hijos, hermanos
   * y parejas. Es la vecindad "de un salto" en la red familiar.
   */
  vecinos(h: string): Set<string> {
    const out = new Set<string>();
    for (const x of this.progenitoresPlanos(h)) out.add(x);
    for (const x of this.hijosDe(h)) out.add(x);
    for (const x of this.hermanos(h)) out.add(x.otra);
    for (const v of this.parejas(h)) out.add(v.otra);
    out.delete(h);
    return out;
  }
}

// ---------------------------------------------------------------------------

function empujar<T>(m: Map<string, T[]>, k: string, v: T): void {
  const ls = m.get(k);
  if (ls) ls.push(v);
  else m.set(k, [v]);
}

/** Cuanto "pesa" cada clase de hermandad: menor es mas cercano. */
function rango(c: ClaseHermandad): number {
  switch (c) {
    case "completos":
      return 0;
    case "medio-paterno":
    case "medio-materno":
      return 1;
    case "declarados":
      return 2;
  }
}
